import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare, X } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { useToast } from '@/context/ToastContext';
import {
  CONSTRAINT_META, CARRIER_PREF_META, CONDITION_LABELS, TRANSPORT_LABELS, ccySym,
} from '@/utils/listingParsers';
import { normalizeCurrencies } from '@/utils/currencyUtils';
import { tripPricingMode, priceLabel } from '@/utils/propositionPricing';
import { usePropositionDraft } from '@/hooks/usePropositionDraft';
import { cityFlag as flag } from '@/utils/cityFlag';
import CurrencySelector from '@/components/currency/CurrencySelector';
import ListingChooser from '@/components/ListingChooser';
import { useListingChoice } from '@/hooks/useListingChoice';
import VerificationGate from '@/components/verification/VerificationGate';
import CommissionPreview from '@/components/CommissionPreview';
import SenderFeeSummary from '@/components/SenderFeeSummary';
import { canDo } from '@/utils/verifications';
import { ROLE_LABELS, roleRequestPath } from '@/utils/roles';
import '@/styles/roles.css';
import { BACKEND_URL } from '@/config';

const API = `${BACKEND_URL}/api`;

/** Couleur rouge au vin utilisée pour le bouton "Négocier" */
const WINE = '#722f37';
const DIFF_COLOR = { 'dp-diff--under': '#075044', 'dp-diff--over': '#dc2626', 'dp-diff--eq': '#6B7280' };

/**
 * ProposeModal — modale d'envoi de proposition / contre-offre.
 *
 * Props :
 *  - open         : boolean
 *  - onClose      : () => void
 *  - meSide       : 'sender' (la cible est un trajet) | 'carrier' (la cible est un colis)
 *  - target       : objet trajet ou annonce ciblé
 *  - onSuccess    : (data) => void
 *  - mode         : 'direct' (« Proposer », prix verrouillé) | 'negotiation' (« Négocier », prix éditable + DM)
 */
export default function ProposeModal({ open, onClose, meSide, target, onSuccess, mode: initMode = 'direct' }) {
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [myListings, setMyListings] = useState([]);
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState(initMode);
  const isNegotiation = mode === 'negotiation';
  const [pendingMyIds, setPendingMyIds] = useState(new Set());
  const [targetUnavailable, setTargetUnavailable] = useState(false);
  // Choix de mon annonce : même composant et mêmes règles que la page d'une annonce (présélection de la plus
  // pertinente, annonce trop lourde non choisissable, « Déjà proposé »)
  const { ranked, selectedId, selected, choose, reset } = useListingChoice({
    meSide, target, listings: myListings, proposedIds: pendingMyIds,
  });

  // Synchronise si le parent change le mode (ex : deux boutons différents)
  useEffect(() => { setMode(initMode); }, [initMode]);

  const location = useLocation();
  const needsRole = meSide === 'sender' ? 'ROLE_SENDER' : 'ROLE_CARRIER';
  const hasRole = (user?.roles || []).includes(needsRole);

  // Prix, devise, garde-fous et requête : brouillon commun aux quatre écrans de proposition
  const mine = selected?.listing || null;
  const draft = usePropositionDraft({ meSide, target, mine });
  const { price, setPrice, currency, setCurrency } = draft;
  const { targetPrice } = draft;

  useEffect(() => {
    if (!open) return;
    setError(''); setMessage(''); reset();
    setMode(initMode);
    setPendingMyIds(new Set());
    setTargetUnavailable(false);
    if (!hasRole) return;
    setLoading(true);
    const listingEndpoint = meSide === 'sender' ? `${API}/shipments/mine` : `${API}/trips/mine`;
    const targetEndpoint = target?.id
      ? `${API}/${meSide === 'sender' ? 'trips' : 'shipments'}/${target.id}`
      : null;
    const VALID_TARGET = meSide === 'sender'
      ? ['PUBLISHED', 'ACTIVE']
      : ['PUBLISHED'];
    Promise.all([
      axios.get(listingEndpoint),
      target?.id ? axios.get(`${API}/propositions/sent?status=PENDING&limit=50`) : Promise.resolve({ data: { data: [] } }),
      targetEndpoint ? axios.get(targetEndpoint) : Promise.resolve({ data: {} }),
    ])
      .then(([listRes, propRes, targetRes]) => {
        const items = Array.isArray(listRes.data) ? listRes.data : (listRes.data?.data || []);
        const filtered = items.filter(i =>
          meSide === 'sender'
            ? i.status === 'PUBLISHED'
            : ['PUBLISHED', 'ACTIVE'].includes(i.status)
        );
        setMyListings(filtered);

        const props = propRes.data?.data || [];
        const pendingIds = props
          .filter(p => meSide === 'sender' ? p.trip_id === target?.id : p.shipment_id === target?.id)
          .map(p => meSide === 'sender' ? p.shipment_id : p.trip_id);
        setPendingMyIds(new Set(pendingIds));

        const freshStatus = targetRes.data?.status;
        if (freshStatus && !VALID_TARGET.includes(freshStatus)) {
          setTargetUnavailable(true);
        }
      })
      .catch(() => setError('Impossible de charger vos annonces.'))
      .finally(() => setLoading(false));
  }, [open, hasRole, meSide]); // eslint-disable-line react-hooks/exhaustive-deps


  if (!open) return null;

  const hasPending = selectedId ? pendingMyIds.has(selectedId) : false;
  // Rien à accepter tel quel (trajet au tarif libre, colis sans budget) : négociation seulement
  const isLibre = meSide === 'sender' && tripPricingMode(target) === 'libre';
  const directUnavailable = !isNegotiation && draft.noPriceToAccept;
  // Proposition directe dans une autre devise : c'est le prix converti qui part — sans lui, rien ne part (D14)
  const conversionMissing = !isNegotiation && draft.conversionMissing;
  const cannotSubmit = !!selectedId && !draft.canSubmit(mode);

  // Devises : listing sélectionné vs target — pour le CurrencySelector
  const myCcys    = normalizeCurrencies(mine);
  const theirCcys = normalizeCurrencies(target);
  // Symbole de la devise choisie (affichage dans le champ prix)
  const cSym = ccySym(currency || target?.primary_currency || 'EUR');
  const negDiff = draft.diff;

  const routeLabel = target?.departure_city && target?.arrival_city
    ? `${target.departure_city} → ${target.arrival_city}` : '·';
  // Champs structurés de l'annonce visée (N4 : plus d'ancien format)
  const shipConstraints    = Array.isArray(target?.constraints) ? target.constraints : [];
  const shipConstraintCustom = target?.constraint_custom || '';
  const shipCarrierPrefs   = Array.isArray(target?.carrier_preferences) ? target.carrier_preferences : [];
  const shipMsg            = target?.message_to_carriers || '';
  const isFirmBudget       = target?.counter_offers === false;
  const isUrgent           = shipConstraints.includes('urgent');
  const priceSym           = ccySym(target?.primary_currency || 'EUR');

  const createHref = meSide === 'sender' ? '/app/shipments/new' : '/app/trips/new';

  const submit = async () => {
    if (!selectedId) {
      setError(meSide === 'sender' ? "Choisissez l'annonce concernée." : 'Choisissez le trajet à proposer.');
      return;
    }
    if (cannotSubmit) return;
    setSubmitting(true); setError('');
    try {
      const payload = draft.payload(mode);
      if (message.trim()) payload.message = message.trim();
      const res = await axios.post(`${API}/propositions`, payload);

      // En mode Négocier : ouvrir un DM et envoyer la contre-offre en chat
      if (isNegotiation && target?.user_id) {
        try {
          const dm = await axios.post(`${API}/conversations/direct`, { user_id: target.user_id });
          const convId = dm.data.id;
          const txMode = meSide === 'sender' ? tripPricingMode(target) : 'kg';
          const offerMsg = [
            `💬 Contre-offre : ${price} ${cSym} pour ${routeLabel}`,
            meSide === 'carrier'
              ? (target?.price_offered ? `Budget proposé : ${target.price_offered} ${ccySym(target?.primary_currency || 'EUR')}` : null)
              : (txMode === 'libre' ? 'Tarif libre' : (targetPrice ? `Tarif ${txMode === 'fixe' ? 'fixe' : 'au kg'} : ${priceLabel(targetPrice.amount)} ${ccySym(targetPrice.currency)}` : null)),
            message.trim() ? `Message : ${message.trim()}` : null,
            `→ Référence proposition #${res.data?.id?.slice(-6) || '...'}`,
          ].filter(Boolean).join('\n');
          await axios.post(`${API}/conversations/${convId}/messages`, { content: offerMsg });
        } catch { /* DM non bloquant */ }
      }

      toast.success(isNegotiation ? 'Contre-offre envoyée ✨' : 'Proposition envoyée ✨');
      onSuccess?.(res.data);
      onClose();
    } catch (e) {
      const detail = e?.response?.data?.detail || e?.response?.data?.message || e?.message || "Erreur lors de l'envoi.";
      setError(typeof detail === 'string' ? detail : JSON.stringify(detail));
    } finally {
      setSubmitting(false);
    }
  };

  const header = (
    <div className="pm-head">
      <div>
        <h3 className="pm-title">
          {isNegotiation
            ? (meSide === 'sender' ? 'Négocier pour ce trajet' : 'Négocier pour cette annonce')
            : (meSide === 'sender' ? 'Proposer mon colis pour ce trajet' : 'Proposer mon trajet pour ce colis')}
        </h3>
        <p className="pm-sub">
          {target?.departure_city && target?.arrival_city
            ? <>{flag(target.departure_city)} {target.departure_city} → {flag(target.arrival_city)} {target.arrival_city}</>
            : '·'}
        </p>
      </div>
      <button type="button" className="pm-close" onClick={onClose} aria-label="Fermer" data-testid="propose-close">
        <X size={16} aria-hidden="true" />
      </button>
    </div>
  );

  if (user && !canDo(user, 'propose')) {
    return (
      <div className="pm-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="propose-modal">
        <div className="pm-modal">
          {header}
          <div className="pm-body">
            <VerificationGate user={user} action="propose" variant="panel" />
          </div>
          <div className="pm-foot">
            <button type="button" className="pm-btn pm-btn-ghost" onClick={onClose} data-testid="propose-cancel">Fermer</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pm-backdrop" onClick={(e) => e.target === e.currentTarget && onClose()} data-testid="propose-modal">
      <div className="pm-modal">
        {header}

        <div className="pm-body">
          {error && <div className="pm-error">{error}</div>}

          {targetUnavailable && (
            <div style={{ display:'flex', alignItems:'flex-start', gap:8, padding:'10px 14px', background:'#fef2f2', border:'1px solid #fca5a5', borderRadius:8, marginBottom:14, fontSize:13 }}>
              <span style={{ fontSize:16, flexShrink:0 }}>🚫</span>
              <span style={{ color:'#b91c1c' }}>
                <strong>Cette annonce n'est plus disponible.</strong> Elle a été fermée, annulée ou prise en charge depuis votre dernière consultation. Revenez à la liste pour voir les annonces actuellement ouvertes.
              </span>
            </div>
          )}

          {/* Mode badge */}
          {isNegotiation ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: '#fdf2f4', border: `1px solid ${WINE}33`, borderRadius: 8, marginBottom: 14, fontSize: 12 }}>
              <span style={{ fontSize: 16 }}>💬</span>
              <span style={{ color: WINE, fontWeight: 600 }}>Mode Négociation</span>
              <span style={{ color: '#6b7280' }}>Modifiez le prix pour proposer une contre-offre</span>
            </div>
          ) : directUnavailable ? (
            <button
              type="button"
              onClick={() => setMode('negotiation')}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', textAlign: 'left', padding: '10px 12px', background: '#fdf2f4', border: `1px solid ${WINE}33`, borderRadius: 8, marginBottom: 14, fontSize: 12, cursor: 'pointer' }}
              data-testid={isLibre ? 'propose-note-libre' : 'propose-note-no-price'}
            >
              <MessageSquare size={18} color={WINE} style={{ flexShrink: 0 }} />
              <span>
                <strong style={{ display: 'block', color: WINE }}>
                  {isLibre ? 'Tarif libre, offre personnalisée' : 'Budget non défini, proposez votre tarif'}
                </strong>
                <span style={{ color: '#6b7280' }}>Passer en mode <strong>Négocier</strong> pour soumettre votre offre</span>
              </span>
            </button>
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--cream)', border: '1px solid var(--border)', borderRadius: 8, marginBottom: 14, fontSize: 12 }}>
              <span style={{ fontSize: 16 }}>🤝</span>
              <span style={{ fontWeight: 600, color: 'var(--ink)' }}>Prix accepté tel quel</span>
              <span style={{ color: 'var(--ink3)' }}>Le montant est verrouillé</span>
            </div>
          )}

          {/* Récapitulatif du target */}
          {target && (
            <div style={{
              padding: '12px 14px', background: 'var(--cream, #faf9f6)',
              border: '1px solid var(--border, #e6e9ef)', borderRadius: 10, marginBottom: 16,
            }}>
              <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink3)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 8 }}>
                {meSide === 'sender' ? '✈️ Trajet ciblé' : '📦 Annonce ciblée'}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
                {meSide === 'carrier' && (
                  <>
                    {target.weight_kg     && <span style={CHIP}>{target.weight_kg} kg</span>}
                    {target.price_offered && <span style={CHIP_G}>{target.price_offered} {priceSym} proposé</span>}
                    {target.dimensions    && <span style={CHIP}>{target.dimensions}</span>}
                    {isFirmBudget         && <span style={CHIP_R}>🔒 Budget fermé</span>}
                    {isUrgent             && <span style={CHIP_R}>🚀 Urgent</span>}
                  </>
                )}
                {meSide === 'sender' && (
                  <>
                    {target.available_weight_kg && <span style={CHIP}>{target.available_weight_kg} kg dispo</span>}
                    {target.price_per_kg        && <span style={CHIP_G}>{target.price_per_kg} {ccySym(target.primary_currency||'EUR')}/kg</span>}
                    {target.transport_modes?.length > 0 && (
                      <span style={CHIP}>{target.transport_modes.map(m => TRANSPORT_LABELS[m] || m).join(', ')}</span>
                    )}
                    {target.counter_offers === false && <span style={CHIP_R}>🔒 Prix ferme</span>}
                  </>
                )}
              </div>
              {/* Contraintes du colis */}
              {(shipConstraints.length > 0 || shipConstraintCustom) && meSide === 'carrier' && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                  {shipConstraints.map(id => {
                    const m = CONSTRAINT_META[id];
                    return m ? <span key={id} style={CHIP}>{m.icon} {m.label}</span> : null;
                  })}
                  {shipConstraintCustom && <span style={CHIP}>📋 {shipConstraintCustom}</span>}
                </div>
              )}
              {/* Préférences transporteur v2 */}
              {shipCarrierPrefs.length > 0 && meSide === 'carrier' && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                  {shipCarrierPrefs.map(id => {
                    const m = CARRIER_PREF_META[id];
                    return m ? <span key={id} style={CHIP_T}>{m.icon} {m.label}</span> : null;
                  })}
                </div>
              )}
              {/* Conditions trajet */}
              {(() => {
                const tripConds = Array.isArray(target?.conditions) ? target.conditions : [];
                return tripConds.length > 0 && meSide === 'sender' ? (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                    {tripConds.map(c => (
                      <span key={c} style={CHIP_T}>{CONDITION_LABELS[c] || c}</span>
                    ))}
                  </div>
                ) : null;
              })()}
              {(shipMsg || (meSide === 'sender' && target?.message_to_senders)) && (
                <div style={{ fontSize: 12, color: 'var(--ink2)', fontStyle: 'italic', marginTop: 6, borderTop: '1px solid var(--border)', paddingTop: 6 }}>
                  « {shipMsg || target?.message_to_senders} »
                </div>
              )}
            </div>
          )}

          {!hasRole ? (
            <div className="pm-summary">
              Pour envoyer cette proposition, il vous faut le rôle <strong>{ROLE_LABELS[needsRole]}</strong>.{' '}
              <Link className="role-request-link" to={roleRequestPath(needsRole, location.pathname)} onClick={onClose} data-testid="propose-request-role">
                Demander le rôle {ROLE_LABELS[needsRole]}
              </Link>
            </div>
          ) : loading ? (
            <div className="pm-summary">Chargement de vos annonces…</div>
          ) : (
            <>
              <div className="pm-field">
                <ListingChooser
                  entries={ranked}
                  selected={selected}
                  selectedId={selectedId}
                  onSelect={choose}
                  meSide={meSide}
                  loading={false}
                  createHref={createHref}
                  createInNewTab
                />
              </div>

              {/* Ordre commun : devise, montant, frais (expéditeur) ou commission (transporteur), puis le reste */}
              {/* Sélecteur de devise — remont quand la sélection de listing change */}
              <div className="pm-field">
                <label>Devise de la transaction</label>
                <CurrencySelector
                  key={selectedId || 'no-listing'}
                  myCurrencies={myCcys}
                  theirCurrencies={theirCcys}
                  theirPrimary={target?.primary_currency}
                  theirFirstName={target?.owner?.first_name || ''}
                  amountCcy={target?.primary_currency || 'EUR'}
                  amount={targetPrice?.amount || 0}
                  value={currency}
                  onChange={setCurrency}
                  onConvert={draft.onTargetConverted}
                  disabled={!selectedId}
                />
              </div>

              <div className="pm-field">
                <label>
                  Prix proposé ({cSym})
                  {!isNegotiation && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--ink3)', fontStyle: 'italic' }}>(verrouillé)</span>}
                  {isNegotiation  && <span style={{ marginLeft: 6, fontSize: 11, color: WINE, fontStyle: 'italic' }}>(modifiable)</span>}
                </label>
                <input
                  type="number"
                  inputMode="decimal"
                  min="0"
                  step="1"
                  value={isNegotiation ? price : (draft.directPrice ?? '')}
                  onChange={(e) => isNegotiation && setPrice(e.target.value)}
                  placeholder={`Ex. 35 ${cSym}`}
                  disabled={!isNegotiation}
                  style={!isNegotiation ? { background: 'var(--c-brume)', color: 'var(--c-encre)', WebkitTextFillColor: 'var(--c-encre)', opacity: 1, cursor: 'not-allowed' }
                    : { borderColor: WINE, boxShadow: `0 0 0 2px ${WINE}22` }}
                  data-testid="propose-price-input"
                />
                {draft.floorNote(mode) && (
                  <div style={{ marginTop: 6, fontSize: 12, color: '#b45309' }} data-testid="propose-price-floor">
                    {draft.floorNote(mode)}
                  </div>
                )}
                {conversionMissing && (
                  <div style={{ marginTop: 6, fontSize: 12, color: '#b45309' }} data-testid="propose-conversion-missing">
                    Conversion indisponible pour le moment : choisissez la devise de l'annonce, ou passez en négociation.
                  </div>
                )}
                {isNegotiation && negDiff && (
                  <div style={{ marginTop: 6, fontSize: 12, color: DIFF_COLOR[negDiff.cls] }} data-testid="propose-price-diff">
                    {negDiff.label}
                  </div>
                )}
              </div>

              {meSide === 'sender' && hasRole && (
                <div className="pm-field">
                  <SenderFeeSummary fee={draft.senderFee(isNegotiation ? 'negotiation' : 'direct')} totalLabel="Total à payer s'il accepte" withTotal />
                </div>
              )}
              {meSide === 'carrier' && hasRole && (
                <div className="pm-field">
                  <CommissionPreview
                    amount={isNegotiation ? (price ? Number(price) : null) : draft.directPrice}
                    currency={currency || target?.primary_currency || 'EUR'}
                  />
                </div>
              )}

              <div className="pm-field">
                <label>Message {isNegotiation ? '(décrivez votre contre-offre)' : '(optionnel)'}</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={600}
                  placeholder={isNegotiation
                    ? 'Expliquez votre contre-offre, vos contraintes ou votre disponibilité…'
                    : 'Présentez-vous, expliquez votre offre, vos contraintes…'}
                  data-testid="propose-message"
                />
              </div>
            </>
          )}
        </div>

        {hasPending && (
          <div style={{ margin: '0 20px 12px', padding: '10px 14px', background: '#fffbeb', border: '1px solid #f59e0b', borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 8 }}>
            <span>⏳</span>
            <span style={{ color: '#92400e' }}>
              Vous avez déjà une proposition en attente pour cette sélection.{' '}
              <button
                type="button"
                onClick={() => { onClose(); navigate('/app/propositions'); }}
                style={{ background: 'none', border: 'none', color: '#d97706', cursor: 'pointer', textDecoration: 'underline', fontSize: 13, padding: 0, fontWeight: 500 }}
              >
                Gérer mes propositions →
              </button>
            </span>
          </div>
        )}
        <div className="pm-foot">
          <button type="button" className="pm-btn pm-btn-ghost" onClick={onClose} disabled={submitting} data-testid="propose-cancel">
            Annuler
          </button>
          <button
            type="button"
            className="pm-btn pm-btn-primary"
            onClick={submit}
            disabled={submitting || !hasRole || myListings.length === 0 || hasPending || targetUnavailable || cannotSubmit}
            style={isNegotiation ? { background: WINE, color: '#fff', borderColor: WINE } : undefined}
            data-testid="propose-submit"
          >
            {submitting
              ? 'Envoi…'
              : isNegotiation
                ? '💬 Envoyer la contre-offre'
                : '🤝 Envoyer la proposition'}
          </button>
        </div>
      </div>
    </div>
  );
}

const CHIP   = { display:'inline-flex', alignItems:'center', gap:3, padding:'3px 9px', borderRadius:99, fontSize:11.5, fontWeight:500, background:'var(--cream,#f5f4f0)', color:'var(--ink2,#3c4a60)', border:'1px solid var(--border,#e6e9ef)' };
const CHIP_T = { ...CHIP, background:'var(--tbg,#ecf7f5)', color:'var(--teal,#0f8a7e)', borderColor:'var(--tlt,#bfe3dd)' };
const CHIP_G = { ...CHIP, background:'#fdf0d0', color:'#92580F', borderColor:'#f0c97a' };
const CHIP_R = { ...CHIP, background:'#fef2f2', color:'#b91c1c', borderColor:'#fca5a5' };
